import { useState, useCallback } from 'react';

interface GeolocationState {
  loading: boolean;
  error: string | null;
  position: { lat: number; lng: number } | null;
}

interface UseGeolocationReturn extends GeolocationState {
  getPosition: () => Promise<{ lat: number; lng: number } | null>;
  isSupported: boolean;
  clearError: () => void;
}

/**
 * Hook for browser geolocation
 *
 * @returns {UseGeolocationReturn} Geolocation state and methods
 *
 * @example
 * const { getPosition, loading, error, position, isSupported } = useGeolocation();
 *
 * const handleGPSClick = async () => {
 *   const coords = await getPosition();
 *   if (coords) {
 *     console.log('Got position:', coords.lat, coords.lng);
 *   }
 * };
 */
export const useGeolocation = (): UseGeolocationReturn => {
  const [state, setState] = useState<GeolocationState>({
    loading: false,
    error: null,
    position: null,
  });

  const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  const getPosition = useCallback(async (): Promise<{ lat: number; lng: number } | null> => {
    if (!isSupported) {
      setState(prev => ({ ...prev, error: 'Geolocation not supported by your browser' }));
      return null;
    }

    setState({ loading: true, error: null, position: null });

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000, // Cache 1 minute
        });
      });

      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      setState({ loading: false, error: null, position: coords });
      return coords;
    } catch (error) {
      const message = error instanceof GeolocationPositionError
        ? getGeolocationErrorMessage(error.code)
        : 'Failed to get your location';

      setState({ loading: false, error: message, position: null });
      return null;
    }
  }, [isSupported]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return { ...state, getPosition, isSupported, clearError };
};

/**
 * Get user-friendly error message from GeolocationPositionError code
 */
const getGeolocationErrorMessage = (code: number): string => {
  switch (code) {
    case 1:
      return 'Location access denied. Please allow location access in your browser settings.';
    case 2:
      return 'Unable to determine your location. Please try again.';
    case 3:
      return 'Location request timed out. Please try again.';
    default:
      return 'An unknown error occurred while getting your location.';
  }
};

export default useGeolocation;
